import React, {useState} from 'react';
import {Badge} from "../../../basic/badge.tsx";
import {Package, ChevronDown} from "lucide-react";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {ColumnDef, DataTable} from "../../../shared/tables/DataTable.tsx";
import {VwpLicense} from "../../../../store/generated/api.ts";
import {
    getLicenseDisplayStatus,
    getLicenseExpirationDisplay,
    isLicenseActiveAndNotExpired
} from "../../../../utils/licenseUtils.ts";
import {LicenseExpirationLabel} from "../../../shared/LicenseExpirationLabel";
import {LicenseStatusChip} from "../../../shared/status/LicenseStatusChip";
import {LicenseQuotas} from "./LicenseQuotas";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "../../../basic/table.tsx";

interface LicenseTableProps {
    licenses: VwpLicense[];
    filter?: "all" | "active" | "expiring";
}

export function LicenseTable({licenses, filter = "all"}: LicenseTableProps) {
    const {t} = useTranslationWithKeys();
    const [collapsedLicenses, setCollapsedLicenses] = useState<Set<string>>(new Set());

    const filteredLicenses = filter === "all"
        ? licenses
        : licenses.filter(l => {
            if (filter === "active") {
                return isLicenseActiveAndNotExpired(l);
            }
            if (filter === "expiring") {
                return getLicenseDisplayStatus(l) === "expiring";
            }
            return false;
        });

    const toggleLicenseExpansion = (licenseId: string) => {
        setCollapsedLicenses(prev => {
            const newSet = new Set(prev);
            if (newSet.has(licenseId)) {
                newSet.delete(licenseId);
            } else {
                newSet.add(licenseId);
            }
            return newSet;
        });
    };

    const columns: ColumnDef<VwpLicense>[] = [
        {
            key: "product",
            header: t('licenses.product'),
            cell: (license) => {
                const expirationDisplay = getLicenseExpirationDisplay(license);
                const hasQuotas = license.quotas && license.quotas.length > 0;
                const isExpanded = !collapsedLicenses.has(license.id || '');

                return (
                    <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-muted-foreground"/>
                        <span>{license.product}</span>
                        {license.trialStatus === "IN_TRIAL" && (
                            <Badge variant="warning" className="text-xs">
                                {t('licenses.trialStatus.inTrial')}
                            </Badge>
                        )}
                        {!expirationDisplay.isExpiredSupport && license.supportLevel === 'SMA' && (
                            <Badge variant="secondary" className="text-xs">
                                {t('licenses.sma')}
                            </Badge>
                        )}
                        {!expirationDisplay.isExpiredSupport && license.supportLevel === 'SMA_PLUS' && (
                            <Badge variant="secondary" className="text-xs">
                                {t('licenses.smaPlus')}
                            </Badge>
                        )}
                        {hasQuotas && (
                            <ChevronDown 
                                className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                            />
                        )}
                    </div>
                );
            }
        },
        {
            key: "id",
            header: t('licenses.licenseId'),
            cell: (license) => license.id || "-"
        },
        {
            key: "quantity",
            header: t('licenses.quantity'),
            cell: (license) => license.quantity
        },
        {
            key: "assigned",
            header: t('licenses.assigned'),
            cell: (license) => {
                const assigned = license.quantity - (license.available || 0);
                return (
                    <span className={assigned === license.quantity ? "text-orange-600" : ""}>
                        {assigned}/{license.quantity}
                    </span>
                );
            }
        },
        {
            key: "available",
            header: t('licenses.available'),
            cell: (license) => license.available || 0
        },
        {
            key: "effectiveExpiryDate",
            header: t('licenses.expires'),
            cell: (license) => <LicenseExpirationLabel license={license}/>
        },
        {
            key: "status",
            header: t('licenses.status'),
            cell: (license) => <LicenseStatusChip license={license}/>
        }
    ];

    return (
        <div className="space-y-4">
            <Table>
                <TableHeader>
                    <TableRow>
                        {columns.map((column) => (
                            <TableHead key={String(column.key)} className={column.className}>
                                {column.header}
                            </TableHead>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {filteredLicenses.map((license) => {
                        const hasQuotas = license.quotas && license.quotas.length > 0;
                        const isExpanded = !collapsedLicenses.has(license.id || '');

                        return (
                            <React.Fragment key={license.id}>
                                <TableRow 
                                    className={hasQuotas ? 'cursor-pointer hover:bg-muted/50' : ''}
                                    onClick={() => hasQuotas && toggleLicenseExpansion(license.id || '')}
                                >
                                    {columns.map((column) => (
                                        <TableCell key={String(column.key)} className={column.className}>
                                            {column.cell ? column.cell(license) : String(license[column.key] ?? "")}
                                        </TableCell>
                                    ))}
                                </TableRow>
                                {hasQuotas && isExpanded && (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} className="p-0">
                                            <LicenseQuotas license={license} />
                                        </TableCell>
                                    </TableRow>
                                )}
                            </React.Fragment>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
}